import { useRef, useEffect } from 'react'
import { motion, useScroll, useTransform, useInView } from 'framer-motion'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ParticleUniverse } from '../three/ParticleUniverse'
import { DNAHelix } from '../three/DNAHelix'
import { 
  BookOpen, 
  Code, 
  Users, 
  Briefcase,
  ChevronRight
} from 'lucide-react'

gsap.registerPlugin(ScrollTrigger)

const storySteps = [
  {
    icon: BookOpen,
    title: 'Learn',
    description: 'Start with interactive lessons designed by industry experts. Our AI adapts to your pace.',
    color: '#5ed29c',
    number: '01',
  },
  {
    icon: Code,
    title: 'Practice',
    description: 'Build real projects in our cloud IDE. Get instant feedback and code reviews.',
    color: '#4fd8ff',
    number: '02',
  },
  {
    icon: Users,
    title: 'Connect',
    description: 'Join study groups, pair program, and get mentored by senior developers.',
    color: '#8b5cf6',
    number: '03',
  },
  {
    icon: Briefcase,
    title: 'Launch',
    description: 'Showcase your portfolio, get interview prep, and land your dream job.',
    color: '#5ed29c',
    number: '04',
  },
]

export function InteractiveStory() {
  const containerRef = useRef<HTMLDivElement>(null)
  const sectionRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(containerRef, { once: true, margin: '-100px' })
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  })
  
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '30%'])

  useEffect(() => {
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: 'top top',
        end: '+=300%',
        pin: true,
        pinSpacing: true,
      })
    }, containerRef)

    return () => ctx.revert()
  }, [])

  return (
    <section
      ref={containerRef}
      id="story"
      className="relative min-h-screen overflow-hidden"
    >
      {/* Background */}
      <motion.div 
        style={{ y: backgroundY }}
        className="absolute inset-0 -z-10"
      >
        <ParticleUniverse />
      </motion.div>
      
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" />

      <div ref={sectionRef} className="relative z-10 min-h-screen flex items-center py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Your Journey to{' '}
              <span className="text-gradient">Mastery</span>
            </h2>
            <p className="text-lg text-white/60 max-w-2xl mx-auto">
              A proven path from beginner to professional developer
            </p>
          </motion.div>

          {/* Story Steps */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Steps */}
            <div className="space-y-8">
              {storySteps.map((step, index) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, x: -40 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="group relative"
                >
                  <div className="flex gap-6 items-start">
                    {/* Number & Line */}
                    <div className="flex flex-col items-center">
                      <div 
                        className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold transition-all duration-300 group-hover:scale-110"
                        style={{ 
                          backgroundColor: `${step.color}20`,
                          color: step.color,
                          boxShadow: `0 0 30px ${step.color}30`
                        }}
                      >
                        {step.number}
                      </div>
                      {index < storySteps.length - 1 && (
                        <div className="w-px h-24 bg-gradient-to-b from-white/20 to-transparent mt-4" />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 pt-2">
                      <div className="flex items-center gap-3 mb-2">
                        <step.icon 
                          className="w-5 h-5" 
                          style={{ color: step.color }} 
                        />
                        <h3 className="text-2xl font-bold text-white group-hover:text-gradient transition-all duration-300">
                          {step.title}
                        </h3>
                      </div>
                      <p className="text-white/60 leading-relaxed mb-4">
                        {step.description}
                      </p>
                      <motion.a
                        href="#"
                        className="inline-flex items-center gap-1 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        style={{ color: step.color }}
                        whileHover={{ x: 5 }}
                      >
                        Learn more <ChevronRight className="w-4 h-4" />
                      </motion.a>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Right - 3D Visualization */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative h-[500px] lg:h-[600px] hidden lg:block"
            >
              <DNAHelix />
              
              {/* Floating Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.8 }}
                className="absolute top-10 right-10 glass rounded-xl p-4 animate-float"
              >
                <div className="text-3xl font-bold text-primary">10K+</div>
                <div className="text-sm text-white/60">Projects Built</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 1 }}
                className="absolute bottom-20 left-10 glass rounded-xl p-4 animate-float"
                style={{ animationDelay: '1s' }}
              >
                <div className="text-3xl font-bold text-secondary">500+</div>
                <div className="text-sm text-white/60">Mentors</div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  )
}